import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ProvisioningService } from '../../services/provisioning/provisioning.service';
import { ToastService } from '../../services/toast/toast.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';

// Manual walk-in provisioning: create a manual requisition (+ visitors +
// transportation), pick rooms from availability, and provision in one submit.
@Component({
  selector: 'app-provision-manual',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './provision-manual.component.html',
  styleUrls: ['./provision-manual.component.scss', '../../../common-style/dropdown.scss'],
})
export class ProvisionManualComponent {
  req: FormGroup;

  categories = ['Regular', 'Utsav'];
  types = ['Individual', 'Family', 'Bulk'];
  genders = ['MALE', 'FEMALE'];

  // room-search chain
  searchType = new FormControl<string>('');
  searchCapacity = new FormControl<number | null>(null);
  searchGender = new FormControl<string>('');
  premiseId = new FormControl<number | null>(null);
  accommodationSetId = new FormControl<number | null>(null);

  premises: any[] = [];
  sets: any[] = [];
  rooms: any[] = [];
  selectedRooms: any[] = [];

  isLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private provisioningService: ProvisioningService,
    private toastService: ToastService
  ) {
    this.req = this.fb.group({
      requisitionFromDate: ['', Validators.required],
      requisitionToDate: ['', Validators.required],
      requisitionCategory: ['Regular', Validators.required],
      requisitionType: ['Family', Validators.required],
      requisitionGuestCount: [null, [Validators.required, Validators.min(1)]],
      requisitionMaleCount: [null],
      requisitionFemaleCount: [null],
      requisitionChildCount: [null],
      familyCode: [''],
      referredBy: [''],
      requisitionPurposeCategory: [''],
      requisitionPurposeDescription: [''],
      attachedMandir: [''],
      utsavCode: [''],
      utsavYear: [''],
      isWorkerIncluded: [false],
      visitors: this.fb.array([this.newVisitor(true)]),
      transportation: this.fb.array([]),
    });
  }

  get visitors(): FormArray {
    return this.req.get('visitors') as FormArray;
  }

  get transportation(): FormArray {
    return this.req.get('transportation') as FormArray;
  }

  private newVisitor(primary = false): FormGroup {
    return this.fb.group({
      visitorFirstName: ['', Validators.required],
      visitorLastName: ['', Validators.required],
      visitorGender: ['MALE', Validators.required],
      visitorMobile: ['', Validators.required],
      visitorFamilyCode: [''],
      visitorAge: [''],
      visitorIdentityType: [''],
      visitorIdentityRefNum: [''],
      visitorRelationship: [''],
      visitorCity: [''],
      visitorState: [''],
      isPrimaryVisitor: [primary],
    });
  }

  addVisitor(): void {
    this.visitors.push(this.newVisitor());
  }

  removeVisitor(i: number): void {
    if (this.visitors.length > 1) this.visitors.removeAt(i);
  }

  private newTransport(): FormGroup {
    return this.fb.group({
      modeOfTransportation: [''],
      ticketOrPNRDetail: [''],
      vehicleNumber: [''],
      driverFullName: [''],
      driverLicenseNumber: [''],
      driverRequireAccomodation: [false],
    });
  }

  addTransport(): void {
    this.transportation.push(this.newTransport());
  }

  removeTransport(i: number): void {
    this.transportation.removeAt(i);
  }

  // -- room search chain --
  private searchFilters(): any {
    return {
      fromDate: this.req.value.requisitionFromDate,
      toDate: this.req.value.requisitionToDate,
      accommodationType: this.searchType.value || null,
      capacity: this.searchCapacity.value || null,
      gender: this.searchGender.value || null,
    };
  }

  loadPremises(): void {
    if (!this.req.value.requisitionFromDate || !this.req.value.requisitionToDate) {
      this.toastService.error('Set the requisition from/to dates first.');
      return;
    }
    this.isLoading = true;
    this.provisioningService.manualPremises(this.searchFilters()).subscribe({
      next: (res: any) => {
        this.premises = res?.data || [];
        this.sets = [];
        this.rooms = [];
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load premises.');
      },
    });
  }

  loadSets(): void {
    if (!this.premiseId.value) return;
    this.provisioningService
      .manualAccommodationSets({ ...this.searchFilters(), premiseId: this.premiseId.value })
      .subscribe({
        next: (res: any) => {
          this.sets = res?.data || [];
          this.rooms = [];
        },
        error: (err) => this.toastService.error(err?.error?.message || 'Failed to load sets.'),
      });
  }

  loadRooms(): void {
    if (!this.premiseId.value || !this.accommodationSetId.value) return;
    this.provisioningService
      .manualRooms({
        provisioningDate: this.req.value.requisitionFromDate,
        premiseId: this.premiseId.value,
        accommodationSetId: this.accommodationSetId.value,
        capacity: this.searchCapacity.value || null,
        gender: this.searchGender.value || null,
      })
      .subscribe({
        next: (res: any) => (this.rooms = res?.data || []),
        error: (err) => this.toastService.error(err?.error?.message || 'Failed to load rooms.'),
      });
  }

  addRoom(room: any): void {
    if (this.selectedRooms.some((r) => r.roomcatalogueInstanceId === room.roomcatalogueInstanceId)) {
      this.toastService.error('Room already added.');
      return;
    }
    this.selectedRooms.push(room);
  }

  removeRoom(i: number): void {
    this.selectedRooms.splice(i, 1);
  }

  private toDdMmYyyy(date: string): string {
    if (!date) return '';
    const [y, m, d] = date.split('-');
    return `${d}-${m}-${y}`;
  }

  submit(): void {
    if (this.req.invalid) {
      this.toastService.error('Fill the required requisition and visitor fields.');
      return;
    }
    if (!this.selectedRooms.length) {
      this.toastService.error('Select at least one room to provision.');
      return;
    }

    const v = this.req.value;
    const payload: any = {
      requisitionFromDate: v.requisitionFromDate,
      requisitionToDate: v.requisitionToDate,
      requisitionMode: 'Manual',
      requisitionType: v.requisitionType,
      requisitionCategory: v.requisitionCategory,
      requisitionGuestCount: Number(v.requisitionGuestCount),
      requisitionMaleCount: v.requisitionMaleCount ?? undefined,
      requisitionFemaleCount: v.requisitionFemaleCount ?? undefined,
      requisitionChildCount: v.requisitionChildCount ?? undefined,
      familyCode: v.familyCode || undefined,
      referredBy: v.referredBy || undefined,
      requisitionPurposeCategory: v.requisitionPurposeCategory || undefined,
      requisitionPurposeDescription: v.requisitionPurposeDescription || undefined,
      attachedMandir: v.attachedMandir || undefined,
      utsavCode: v.utsavCode || undefined,
      utsavYear: v.utsavYear || undefined,
      isAdmin: false,
      isWorkerIncluded: v.isWorkerIncluded === true,
      manualRequisitionVisitorDetails: v.visitors,
      transportationDetails: (v.transportation || []).map((t: any) => ({
        modeOfTransportation: t.modeOfTransportation || undefined,
        ticketOrPNRDetail: t.ticketOrPNRDetail || undefined,
        trasnportationByRoadDetail: {
          vehicleNumber: t.vehicleNumber || undefined,
          driverFullName: t.driverFullName || undefined,
          driverLicenseNumber: t.driverLicenseNumber || undefined,
          driverRequireAccomodation: t.driverRequireAccomodation === true,
        },
      })),
      manualProvisionDetail: this.selectedRooms.map((r) => ({
        provisioningDate: this.toDdMmYyyy(v.requisitionFromDate),
        roomcatalogueInstanceDetail: {
          premisesId: r.premiseId,
          premisesName: r.premiseName,
          roomcatalogueInstanceId: r.roomcatalogueInstanceId,
          accommodationType: r.accommodationType,
          accommodationAbbrevName: r.accommodationCode,
          capacity: r.totalCapacity,
        },
      })),
    };

    this.isLoading = true;
    this.provisioningService.manualProvision(payload).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Manual provisioning done.');
        this.isLoading = false;
        this.reset();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Manual provisioning failed.');
      },
    });
  }

  private reset(): void {
    this.req.reset({
      requisitionCategory: 'Regular',
      requisitionType: 'Family',
      isWorkerIncluded: false,
    });
    this.visitors.clear();
    this.visitors.push(this.newVisitor(true));
    this.transportation.clear();
    this.premises = [];
    this.sets = [];
    this.rooms = [];
    this.selectedRooms = [];
    this.premiseId.setValue(null);
    this.accommodationSetId.setValue(null);
  }
}
